/**
 * Turning a device GPS fix into something a form can save, and a person can
 * judge.
 *
 * Gareth 2026-08-23: "a lot of the addresses produce coordinates in the area
 * not exact locations" — geocoding a rural address gets you the parish, not the
 * farm gate. Standing at the place with a phone is the only way to pin some of
 * these, so sites, depots and hotels (everything the map plots from an address)
 * all offer it.
 *
 * The wording lives here rather than in the three forms because the copy IS the
 * feature: someone reading it is standing in a field deciding whether to trust
 * the number, and all three surfaces must say the same thing.
 */

/**
 * Above this, a device fix is too coarse to be worth more than the geocoded
 * address it replaces, so we warn rather than confirm. 50m is about the width
 * of a farmyard — inside that you are on the right buildings.
 */
export const DEVICE_ACCURACY_WARN_METRES = 50;

// Phrased for someone outdoors, not a developer: each says what to do next.
// Keys match getDeviceLocationDetailed's reason codes.
export const DEVICE_LOCATION_ERRORS = {
  denied: 'Location is blocked for this site. Allow location in your browser settings, then try again.',
  unavailable: 'Your device could not get a position. On a phone, check location services are on.',
  timeout: 'Timed out waiting for a position. Signal is often poor inside sheds — step outside and try again.',
  unsupported: 'This browser cannot share a location. Enter the coordinates by hand instead.'
};

/**
 * Normalises what getDeviceLocationDetailed returns into a form-ready result.
 *
 * Failure -> { ok: false, status: 'error', message }
 * Success -> { ok: true, status: 'success' | 'warning', message, lat, lng,
 *              accuracy, accuracyLabel }
 *
 * lat/lng come back as STRINGS at 7dp: that matches the number inputs' step and
 * is ~1cm, far finer than any phone fix, so nothing real is rounded away.
 *
 * @param {{location: ?{lat: number, lng: number, accuracy: ?number}, reason: ?string}} fix
 * @param {string} noun - what is being pinned, for the message ("site", "depot", "stay")
 */
export function describeDeviceFix(fix, noun = 'record') {
  const { location, reason } = fix || {};

  if (!location) {
    return {
      ok: false,
      status: 'error',
      message: DEVICE_LOCATION_ERRORS[reason] || DEVICE_LOCATION_ERRORS.unavailable
    };
  }

  const { lat, lng } = location;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, status: 'error', message: 'Your device returned a position we could not read. Try again.' };
  }
  // 0,0 is the Atlantic, and is what a broken sensor reports. The site form
  // rejects it on save anyway; catching it here gives a better message.
  if (lat === 0 && lng === 0) {
    return { ok: false, status: 'error', message: 'Your device reported 0,0, which is not a real position. Try again.' };
  }

  const accuracy = Number.isFinite(location.accuracy) ? Math.round(location.accuracy) : null;
  const vague = accuracy != null && accuracy > DEVICE_ACCURACY_WARN_METRES;

  return {
    ok: true,
    status: vague ? 'warning' : 'success',
    lat: String(Number(lat.toFixed(7))),
    lng: String(Number(lng.toFixed(7))),
    accuracy,
    // Stored in geocode_accuracy where the table has one, so the provenance of
    // a pin survives in the database, not just on screen.
    accuracyLabel: accuracy != null ? `device ±${accuracy}m` : 'device',
    message: vague
      // A phone in a steel-framed parlour can be hundreds of metres out. Say so
      // rather than showing a confident green tick.
      ? `Pinned your position, but only to about ±${accuracy}m — that is a rough fix. Step outside, away from thick walls, and try again for a tighter one. Save the ${noun} to keep it.`
      : `Pinned your current position${accuracy != null ? ` (±${accuracy}m)` : ''}. Save the ${noun} to confirm it.`
  };
}

/**
 * What a device pin writes to the geocode_* columns.
 *
 * geocode_status MUST be 'manual': api/src/database/geocode-missing-sites.js
 * re-geocodes every farm_sites row whose status is NOT 'manual', so without
 * this a walked-to fix is silently replaced by a postcode centroid on the next
 * bulk run — the very problem this feature exists to fix. Depots and
 * accommodation are not touched by that job today, but they use the same shape
 * so the guard is not something a future bulk job has to rediscover per table.
 *
 * geocode_source records that a human stood there, which 'manual' alone cannot
 * distinguish from someone typing numbers into the boxes.
 */
export function deviceGeocodeFields(result) {
  return {
    geocode_status: 'manual',
    geocode_source: 'device_gps',
    geocode_accuracy: result.accuracyLabel
  };
}
